import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const layoutTypes = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical',
  TWOCOLUMN: 'twocolumn',
} as const;

const layoutModeTypes = {
  LIGHTMODE: 'light',
  DARKMODE: 'dark',
} as const;

const leftSidebarTypes = {
  LIGHT: 'light',
  DARK: 'dark',
  GRADIENT: 'gradient',
  GRADIENT_2: 'gradient-2',
  GRADIENT_3: 'gradient-3',
  GRADIENT_4: 'gradient-4',
} as const;

const layoutWidthTypes = {
  FLUID: 'lg',
  BOXED: 'boxed',
} as const;

const layoutPositionTypes = {
  FIXED: 'fixed',
  SCROLLABLE: 'scrollable',
} as const;

const topbarThemeTypes = {
  LIGHT: 'light',
  DARK: 'dark',
} as const;

const leftsidbarSizeTypes = {
  DEFAULT: 'lg',
  COMPACT: 'md',
  SMALLICON: 'sm',
  SMALLHOVER: 'sm-hover',
} as const;

const leftSidebarViewTypes = {
  DEFAULT: 'default',
  DETACHED: 'detached',
} as const;

const leftSidebarImageTypes = {
  NONE: 'none',
  IMG1: 'img-1',
  IMG2: 'img-2',
  IMG3: 'img-3',
  IMG4: 'img-4',
} as const;

const preloaderTypes = {
  ENABLE: 'enable',
  DISABLE: 'disable',
} as const;

type LayoutType = typeof layoutTypes[keyof typeof layoutTypes];
type LayoutModeType = typeof layoutModeTypes[keyof typeof layoutModeTypes];
type LeftSidebarType = typeof leftSidebarTypes[keyof typeof leftSidebarTypes];
type LayoutWidthType = typeof layoutWidthTypes[keyof typeof layoutWidthTypes];
type LayoutPositionType = typeof layoutPositionTypes[keyof typeof layoutPositionTypes];
type TopbarThemeType = typeof topbarThemeTypes[keyof typeof topbarThemeTypes];
type LeftsidbarSizeType = typeof leftsidbarSizeTypes[keyof typeof leftsidbarSizeTypes];
type LeftSidebarViewType = typeof leftSidebarViewTypes[keyof typeof leftSidebarViewTypes];
type LeftSidebarImageType = typeof leftSidebarImageTypes[keyof typeof leftSidebarImageTypes];
type PreloaderType = typeof preloaderTypes[keyof typeof preloaderTypes];

const changeHTMLAttribute = (attribute: string, value: string): void => {
  if (document.documentElement) {
    document.documentElement.setAttribute(attribute, value);
  }
};

interface LayoutStore {
  layoutType: LayoutType;
  leftSidebarType: LeftSidebarType;
  layoutModeType: LayoutModeType;
  layoutWidthType: LayoutWidthType;
  layoutPositionType: LayoutPositionType;
  topbarThemeType: TopbarThemeType;
  leftsidbarSizeType: LeftsidbarSizeType;
  leftSidebarViewType: LeftSidebarViewType;
  leftSidebarImageType: LeftSidebarImageType;
  preloader: PreloaderType;

  changeLayout: (layout: LayoutType) => void;
  changeLayoutMode: (mode: LayoutModeType) => void;
  changeSidebarTheme: (theme: LeftSidebarType) => void;
  changeLayoutWidth: (layoutWidth: LayoutWidthType) => void;
  changeLayoutPosition: (layoutPosition: LayoutPositionType) => void;
  changeTopbarTheme: (topbarTheme: TopbarThemeType) => void;
  changeLeftsidebarSizeType: (sizeType: LeftsidbarSizeType) => void;
  changeLeftsidebarViewType: (viewType: LeftSidebarViewType) => void;
  changeSidebarImageType: (imageType: LeftSidebarImageType) => void;
  changePreLoader: (preloader: PreloaderType) => void;
  resetValue: () => void;
}

const INIT_STATE = {
  layoutType: layoutTypes.VERTICAL,
  leftSidebarType: leftSidebarTypes.DARK,
  layoutModeType: layoutModeTypes.LIGHTMODE,
  layoutWidthType: layoutWidthTypes.FLUID,
  layoutPositionType: layoutPositionTypes.FIXED,
  topbarThemeType: topbarThemeTypes.LIGHT,
  leftsidbarSizeType: leftsidbarSizeTypes.DEFAULT,
  leftSidebarViewType: leftSidebarViewTypes.DEFAULT,
  leftSidebarImageType: leftSidebarImageTypes.NONE,
  preloader: preloaderTypes.DISABLE,
};

export const useLayoutStore = create<LayoutStore>()(
  persist(
    (set) => ({
      ...INIT_STATE,

      changeLayout: (layout: LayoutType) => {
        set({ layoutType: layout });
        if (layout === 'twocolumn') {
          document.documentElement.removeAttribute('data-layout-width');
        } else if (layout === 'horizontal') {
          document.documentElement.removeAttribute('data-sidebar-size');
        }
        changeHTMLAttribute('data-layout', layout);
      },

      changeLayoutMode: (mode: LayoutModeType) => {
        set({ layoutModeType: mode });
        changeHTMLAttribute('data-layout-mode', mode);
      },

      changeSidebarTheme: (theme: LeftSidebarType) => {
        set({ leftSidebarType: theme });
        changeHTMLAttribute('data-sidebar', theme);
      },

      changeLayoutWidth: (layoutWidth: LayoutWidthType) => {
        set({ layoutWidthType: layoutWidth });
        if (layoutWidth === 'lg') {
          changeHTMLAttribute('data-layout-width', 'fluid');
        } else {
          changeHTMLAttribute('data-layout-width', 'boxed');
        }
      },

      changeLayoutPosition: (layoutPosition: LayoutPositionType) => {
        set({ layoutPositionType: layoutPosition });
        changeHTMLAttribute('data-layout-position', layoutPosition);
      },

      changeTopbarTheme: (topbarTheme: TopbarThemeType) => {
        set({ topbarThemeType: topbarTheme });
        changeHTMLAttribute('data-topbar', topbarTheme);
      },

      changeLeftsidebarSizeType: (sizeType: LeftsidbarSizeType) => {
        set({ leftsidbarSizeType: sizeType });
        switch (sizeType) {
          case 'lg':
            changeHTMLAttribute('data-sidebar-size', 'lg');
            break;
          case 'md':
            changeHTMLAttribute('data-sidebar-size', 'md');
            break;
          case 'sm':
            changeHTMLAttribute('data-sidebar-size', 'sm');
            break;
          case 'sm-hover':
            changeHTMLAttribute('data-sidebar-size', 'sm-hover');
            break;
          default:
            changeHTMLAttribute('data-sidebar-size', 'lg');
        }
      },

      changeLeftsidebarViewType: (viewType: LeftSidebarViewType) => {
        set({ leftSidebarViewType: viewType });
        changeHTMLAttribute('data-layout-style', viewType);
      },

      changeSidebarImageType: (imageType: LeftSidebarImageType) => {
        set({ leftSidebarImageType: imageType });
        changeHTMLAttribute('data-sidebar-image', imageType);
      },

      changePreLoader: (preloader: PreloaderType) => {
        set({ preloader });
        changeHTMLAttribute('data-preloader', preloader);
      },

      resetValue: () => {
        set(INIT_STATE);
      },
    }),
    {
      name: 'layout-storage',
      partialize: (state) => ({
        layoutType: state.layoutType,
        leftSidebarType: state.leftSidebarType,
        layoutModeType: state.layoutModeType,
        layoutWidthType: state.layoutWidthType,
        layoutPositionType: state.layoutPositionType,
        topbarThemeType: state.topbarThemeType,
        leftsidbarSizeType: state.leftsidbarSizeType,
        leftSidebarViewType: state.leftSidebarViewType,
        leftSidebarImageType: state.leftSidebarImageType,
        preloader: state.preloader,
      }),
    }
  )
);
